import React, { useState } from 'react';
import {
  MdArrowBack, MdShield, MdReceiptLong, MdAccountBalance,
  MdEventAvailable, MdLightbulbOutline, MdCheckCircleOutline,
} from 'react-icons/md';

// Mock notification feed. Real source TBD — likely a /api/notifications
// endpoint that aggregates fraud alerts, payment due dates, statement
// readiness, and product nudges. The shape mirrors what such an endpoint
// would return so swapping is mechanical.
const INITIAL_NOTIFS = [
  {
    id: 'n1',
    type: 'fraud',
    title: 'Verify a charge on your Visa',
    body: 'A $244.18 purchase at Apple Store · Toronto looks unusual. Tap to confirm or report it.',
    time: 'Just now',
    unread: true,
  },
  {
    id: 'n2',
    type: 'payment',
    title: 'Credit card payment due in 3 days',
    body: 'Minimum $48.00 due Friday. Set up auto-pay to skip the reminders.',
    time: '2 hours ago',
    unread: true,
  },
  {
    id: 'n3',
    type: 'statement',
    title: 'May statement is ready',
    body: 'Your TD All-Inclusive Banking statement is available in PDF.',
    time: 'Yesterday',
    unread: true,
  },
  {
    id: 'n4',
    type: 'tip',
    title: 'You\'re close to your savings goal',
    body: 'Home Down Payment is 65% there. Adding $200 this month gets you to 70%.',
    time: '2 days ago',
    unread: false,
  },
  {
    id: 'n5',
    type: 'security',
    title: 'New device signed in',
    body: 'iPhone in Toronto signed in to your account on May 6. If this wasn\'t you, change your password.',
    time: '4 days ago',
    unread: false,
  },
  {
    id: 'n6',
    type: 'success',
    title: 'e-Transfer to Sarah Chen deposited',
    body: '$150.00 was accepted on May 4.',
    time: 'Last week',
    unread: false,
  },
];

const TYPE_META = {
  fraud:     { Icon: MdShield,             color: 'var(--td-red)',     label: 'Security' },
  payment:   { Icon: MdReceiptLong,        color: 'var(--td-amber)',   label: 'Payment'  },
  statement: { Icon: MdAccountBalance,     color: 'var(--td-blue)',    label: 'Account'  },
  tip:       { Icon: MdLightbulbOutline,   color: 'var(--td-gold)',    label: 'Tip'      },
  security:  { Icon: MdEventAvailable,     color: 'var(--td-purple)',  label: 'Security' },
  success:   { Icon: MdCheckCircleOutline, color: 'var(--td-green)',   label: 'Update'   },
};

const NotificationsScreen = ({ onNavigate, onBack }) => {
  const [notifs, setNotifs] = useState(INITIAL_NOTIFS);
  const unreadCount = notifs.filter(n => n.unread).length;

  const markAllRead = () => setNotifs(prev => prev.map(n => ({ ...n, unread: false })));
  const markRead    = (id) => setNotifs(prev => prev.map(n => n.id === id ? { ...n, unread: false } : n));

  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back to home">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Notifications</h1>
        </div>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            style={{
              background: 'rgba(0,0,0,0.12)',
              border: '1px solid rgba(0,0,0,0.16)',
              color: 'var(--bh-black)',
              padding: '6px 10px',
              font: 'var(--bh-caption)',
              fontWeight: 700,
              cursor: 'pointer',
              borderRadius: 'var(--radius-md)',
            }}
            aria-label="Mark all as read"
          >
            Mark all read
          </button>
        )}
      </header>

      <div className="bh-flow-body">
        <div className="bh-form-card" style={{ alignItems: 'flex-start', gap: 'var(--space-3xs)' }}>
          <span style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </span>
          <span style={{ font: 'var(--bh-body-sm)', color: 'var(--bh-dark)' }}>
            We'll only ping you about the things that matter. Manage which alerts you receive in
            <button
              type="button"
              onClick={() => onNavigate('profileScreen')}
              style={{ background: 'transparent', border: 'none', color: 'var(--td-green)', font: 'inherit', cursor: 'pointer', padding: '0 4px' }}
            >Profile</button>
            .
          </span>
        </div>

        <div className="bh-tx-list">
          {notifs.map(n => {
            const meta = TYPE_META[n.type] || TYPE_META.tip;
            const { Icon } = meta;
            return (
              <button
                key={n.id}
                type="button"
                className="bh-tx-row"
                style={{
                  textAlign: 'left', cursor: 'pointer', background: 'transparent', border: 'none',
                  width: '100%', padding: 'var(--space-sm)',
                  borderBottom: '1px solid var(--bh-light-gray)',
                }}
                onClick={() => markRead(n.id)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)', flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      width: 36, height: 36,
                      background: meta.color,
                      color: 'var(--bh-white)',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <Icon size={18} />
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2xs)', marginBottom: 2 }}>
                      <span className="bh-tx-merchant" style={{ flex: 1, whiteSpace: 'normal' }}>
                        {n.title}
                      </span>
                      {n.unread && (
                        <span
                          aria-label="Unread"
                          style={{
                            width: 8, height: 8, background: 'var(--td-green)',
                            borderRadius: 'var(--radius-full)', flexShrink: 0,
                          }}
                        />
                      )}
                    </div>
                    <div style={{ font: 'var(--bh-body-sm)', color: 'var(--bh-dark)', lineHeight: 1.4 }}>
                      {n.body}
                    </div>
                    <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)', marginTop: 2 }}>
                      {meta.label} · {n.time}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NotificationsScreen;
